/* eslint-disable import/no-cycle */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { removeAuthToken, selectIsAuthenticated, setAuthToken } from './authTokenSlice';
import { getResyConfigThunk } from './resyConfigSlice';

import {
  getAuthDestroy,
  getUser,
  postAuthEmailRequest,
  postAuthEmailVerify,
  postAuthChallenge,
  postAuthMobile,
  postAuthPassword,
  postRegisterUser,
  postUserLocation,
} from '../api';
import {
  FAILED, IDLE, PENDING, SUCCEEDED,
} from '../constants/asyncLoadingStatuses';
import { isValidLatLong, userHasAmexCard } from '../utils/userSliceUtils';
import { getListVenuesByUserThunk } from './favoriteVenuesSlice';
import { fetchExperiments, resetExperiments } from './experimentsSlice';

const postRegisterUserThunk = createAsyncThunk(
  'user/registerUser',
  async ({ userObj, captchaResponse }, { rejectWithValue }) => {
    try {
      const response = await postRegisterUser(userObj, captchaResponse);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const loadUserThunk = createAsyncThunk(
  'user/getUser',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = await getUser();
      dispatch(getListVenuesByUserThunk());
      return response;
    } catch (error) {
      dispatch(userLogoutThunk());
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthEmailRequestThunk = createAsyncThunk(
  'user/requestEmailCode',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthEmailRequest(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthEmailVerifyThunk = createAsyncThunk(
  'user/verifyEmailCode',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthEmailVerify(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthPasswordThunk = createAsyncThunk(
  'user/authenticatePassword',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthPassword(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthMobileThunk = createAsyncThunk(
  'user/authenticateMobile',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthMobile(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const postAuthChallengeThunk = createAsyncThunk(
  'user/authenticateChallenge',
  async (params, { rejectWithValue }) => {
    try {
      const response = await postAuthChallenge(params);
      return response;
    } catch (error) {
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState: {
    registrationError: null,
    registrationStatus: IDLE,
    reservationToBeModified: undefined,
    shouldUserReservationsUpdate: null,
    userData: null,
    userLocation: {},
  },
  reducers: {
    setReservationToBeModified: (state, action) => {
      const reservation = action.payload;
      state.reservationToBeModified = reservation;
    },
    setShouldUserReservationsUpdate: (state, action) => {
      const params = action.payload;
      state.shouldUserReservationsUpdate = params;
    },
    setUserData: (state, action) => {
      const data = action.payload;
      state.userData = data;
      state.registrationError = null;
    },
    setUserLocation: (state, action) => {
      const location = action.payload;
      state.userLocation = location;
    },
    setUserLocationLatLong: (state, action) => {
      const { lat, long } = action.payload;
      if (isValidLatLong({ lat, long })) {
        state.userLocation.lat = lat;
        state.userLocation.long = long;
      }
    },
    resetRegistration: (state) => {
      state.registrationError = null;
      state.registrationStatus = IDLE;
    },
    resetReservationToBeModified: (state) => {
      state.reservationToBeModified = undefined;
    },
    resetShouldUserReservationsUpdate: (state) => {
      state.shouldUserReservationsUpdate = null;
    },
    resetUserData: (state) => {
      state.userData = null;
      state.registrationError = null;
    },
    setUpdatedPhoneNumber: (state, action) => {
      if (action.payload) {
        state.userData.mobile_number = (action.payload[0] === '+') ? action.payload : `+${action.payload}`;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postRegisterUserThunk.pending, (state) => {
      state.registrationStatus = PENDING;
    });
    builder.addCase(postRegisterUserThunk.fulfilled, (state) => {
      state.registrationStatus = SUCCEEDED;
    });
    builder.addCase(postRegisterUserThunk.rejected, (state, action) => {
      const errorData = action.payload.data.data;
      state.registrationError = errorData;
      state.registrationStatus = FAILED;
    });
  },
});

const {
  resetRegistration,
  resetReservationToBeModified,
  resetShouldUserReservationsUpdate,
  setReservationToBeModified,
  setShouldUserReservationsUpdate,
  setUserData,
  setUserLocation,
  setUserLocationLatLong,
  resetUserData,
  setUpdatedPhoneNumber,
} = userSlice.actions;

// Convenience getter-thunks for Angular controllers only, will be torn down in time
const getReservationToBeModifiedThunk = () => (dispatch, getState) => {
  const { user } = getState();
  return user.reservationToBeModified;
};

// Thunks
// Using function declarations so that names are hoisted,
// thus eliminating the need to juggle ordering to manage dependencies

function getUserThunk(shouldRefresh = false) {
  return (dispatch, getState) => {
    const state = getState();
    const { user: { userData } } = state;
    if ((userData === null || shouldRefresh)) {
      if (selectIsAuthenticated(state)) {
        return dispatch(getResyConfigThunk())
          .then(() => dispatch(loadUserThunk())
            .unwrap()
            .then((data) => {
              dispatch(setUserData(data));
              return data;
            }));
      }
    }
    return Promise.resolve(userData);
  };
}

function registerUserThunk(account, captchaResponse) {
  return (dispatch, getState) => {
    const userObj = { ...account };
    const { user: userData } = getState();
    if (userData?.registrationStatus !== PENDING) {
      return dispatch(postRegisterUserThunk({ userObj, captchaResponse }))
        .then((response) => {
          if (response.error) {
            throw (new Error(`postRegisterUser ERROR: ${response?.payload?.message}`));
          }
          dispatch(doAuthenticationThunk(response?.payload?.user));
          return Promise.resolve(response);
        });
    }
    return Promise.resolve({});
  };
}

function userLogoutThunk() {
  return async (dispatch, getState) => {
    const analyticsPayload = { category: 'Account' };
    const { user: { userData } } = getState();
    if (userData) {
      analyticsPayload.userId = userData.id;
      analyticsPayload.isLoggedIn = false;
      analyticsPayload.email = userData.em_address;
    }
    await getAuthDestroy();
    dispatch(removeAuthToken(analyticsPayload));
    dispatch(resetUserData());
    dispatch(resetExperiments());
    dispatch(fetchExperiments());
  };
}

function requestEmailCodeThunk(params) {
  return (dispatch) => dispatch(postAuthEmailRequestThunk(params))
    .unwrap()
    .then((data) => data);
}

function doAuthenticationThunk(userData) {
  return (dispatch, getState) => {
    const userDataWithAmexCard = { ...userData, amexCard: userHasAmexCard(userData) };
    const { user: { userLocation } } = getState();
    dispatch(setAuthToken(userData?.token));
    dispatch(setUserData(userDataWithAmexCard));
    dispatch(fetchExperiments(userData?.id));
    dispatch(getListVenuesByUserThunk());
    if (isValidLatLong(userLocation)) {
      postUserLocation({
        x: userLocation.long,
        y: userLocation.lat,
      });
    }
    // From User service: Call here to replace reservation.load,
    // though it does not seem to do anything as data was never persisted in User service.
    dispatch(setShouldUserReservationsUpdate({}));
  };
}

function authenticateEmailCodeThunk(params) {
  return (dispatch) => dispatch(postAuthEmailVerifyThunk({ ...params }))
    .unwrap()
    .then((userData) => {
      dispatch(doAuthenticationThunk(userData));
      return userData;
    });
}

function authenticatePasswordThunk(params) {
  return (dispatch) => dispatch(postAuthPasswordThunk({ ...params }))
    .unwrap()
    .then((userData) => {
      dispatch(doAuthenticationThunk(userData));
      return userData;
    });
}

function authenticateMobileThunk(params) {
  return (dispatch) => dispatch(postAuthMobileThunk({ ...params }))
    .unwrap()
    .then((data) => {
      if (data.user) {
        dispatch(doAuthenticationThunk(data.user));
      }
      return data;
    });
}

function authenticateChallengeThunk(params) {
  return (dispatch) => dispatch(postAuthChallengeThunk({ ...params }))
    .unwrap()
    .then((userData) => {
      dispatch(doAuthenticationThunk(userData));
      return userData;
    });
}

// Selectors
const selectIsConcierge = (state) => !!state.user.userData?.is_concierge;
const selectIsGlobalDiningAccess = (state) => !!state.user.userData?.is_global_dining_access;
const selectIsCenturionEligible = (state) => !!state.user.userData?.is_centurion_eligible;
const selectIsPlatinumEligible = (state) => !!state.user.userData?.is_platinum_eligible;
const selectIsPlatinumNight = (state) => !!state.user.userData?.is_platinum_night_eligible;
const selectIsNonUS = (state) => !!state.user.userLocation?.isNonUS;
const selectIsGoldCardMember = (state) => !!state.user.userData?.amex_card_types?.find((item) => item.toLowerCase() === 'gold');
const selectResyAdsId = (state) => state.user.userData?.resy_ads_id;

export {
  authenticateChallengeThunk,
  authenticateEmailCodeThunk,
  authenticateMobileThunk,
  authenticatePasswordThunk,
  getReservationToBeModifiedThunk,
  getUserThunk,
  loadUserThunk,
  postAuthChallengeThunk,
  postAuthEmailRequestThunk,
  postAuthEmailVerifyThunk,
  postAuthMobileThunk,
  postAuthPasswordThunk,
  postRegisterUserThunk,
  registerUserThunk,
  requestEmailCodeThunk,
  resetRegistration,
  resetReservationToBeModified,
  resetShouldUserReservationsUpdate,
  resetUserData,
  selectIsConcierge,
  selectIsGlobalDiningAccess,
  selectIsCenturionEligible,
  selectIsPlatinumEligible,
  selectIsPlatinumNight,
  selectIsGoldCardMember,
  selectIsNonUS,
  selectResyAdsId,
  setReservationToBeModified,
  setShouldUserReservationsUpdate,
  setUserData,
  setUpdatedPhoneNumber,
  setUserLocation,
  setUserLocationLatLong,
  userLogoutThunk,
};

export default userSlice.reducer;
